import React, { useState, useEffect } from 'react'
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Grid
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import {
  IconButtonWithTooltip,
  PersonAutocomplete
} from 'components'
import {
  type Person,
  type PersonBaseBasicInformation,
  type MemberInformation
} from 'services'

export type PersonType = 'member' | 'personUS' | 'personInternational'

interface PersonSelectDialogProps<T extends Person | PersonBaseBasicInformation | MemberInformation> {
  open: boolean
  onClose: () => void
  onPersonSelected: (person: T) => void
  title?: string
  personType?: PersonType
  fullWidth?: boolean
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl'
  minHeight?: string
}

export const PersonSelectDialog = <T extends Person | PersonBaseBasicInformation | MemberInformation>({
  open,
  onClose,
  onPersonSelected,
  title = 'Select Person',
  personType = 'personUS',
  fullWidth = true,
  maxWidth = 'md',
  minHeight = '60vh'
}: PersonSelectDialogProps<T>): React.ReactElement => {
  const [selectedPerson, setSelectedPerson] = useState<T | undefined>(undefined)
  const [isCreatingPerson, setIsCreatingPerson] = useState(false)
  const [isEditing, setIsEditing] = useState(true)

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setSelectedPerson(undefined)
      setIsCreatingPerson(false)
      setIsEditing(true)
    }
  }, [open])

  const handlePersonSelected = (person: T): void => {
    setSelectedPerson(person)
    onPersonSelected(person)
    handleClose()
  }

  const handleClose = (): void => {
    setSelectedPerson(undefined)
    setIsCreatingPerson(false)
    setIsEditing(true)
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullWidth={fullWidth}
      maxWidth={maxWidth}
      PaperProps={{
        sx: {
          minHeight
        }
      }}
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {title}
        <IconButtonWithTooltip
          label="Close"
          color="secondary"
          icon={<CloseIcon />}
          onClick={handleClose}
        />
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ pt: 2 }}>
          <Grid item xs={12}>
            <Grid container alignItems="center" spacing={1}>
              <Grid item xs={12}>
                <PersonAutocomplete<T>
                  person={selectedPerson}
                  updatePerson={handlePersonSelected}
                  filledPerson={false}
                  editable={true}
                  editing={isEditing}
                  setEditing={setIsEditing}
                  createPerson={isCreatingPerson}
                  setCreatePerson={setIsCreatingPerson}
                  personType={personType}
                />
              </Grid>
            </Grid>
          </Grid>

        </Grid>
      </DialogContent>
    </Dialog>
  )
}
